import { useApi } from '@/components/Functional/hooks-core/useApi'
import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { AppMachineContext } from '@/components/Functional/hooks-core/useRaundLifeCycle'
import type { GeoLocationForLock } from '@/components/Functional/utils/calcSpotLockStatus'
import { useStampPointLabelFromSheetTypes } from '@/components/Functional/useStampPointLabel'
import { DialogArPhotoLocked } from '@/components/Pages/spots/components/dialogs/DialogArPhotoLocked'
import { DialogAudioGuideLocked } from '@/components/Pages/spots/components/dialogs/DialogAudioGuideLocked'
import { DialogSpotsAudioGuide } from '@/components/Pages/spots/components/dialogs/DialogSpotsAudioGuide'
import { type SpotData, isAudioGuideLocked, toAudioGuideDialogItems } from '@/schemas/v2/spot'
import { useSelector } from '@xstate/react'
import { useState } from 'react'
import { SpotOverview } from './SpotOverview'

type Props = {
  spots: SpotData[]
  onArPhotoClick?: (spot: SpotData) => void
  geoLocation: GeoLocationForLock
}

export function SpotList({ spots, onArPhotoClick, geoLocation }: Props) {
  const { gen } = useCampaignSlug()
  const [audioGuideSpot, setAudioGuideSpot] = useState<SpotData | null>(null)
  const [audioGuideItems, setAudioGuideItems] = useState<{ caption: string | null; audio: string | null }[] | null>(
    null,
  )
  const [arPhotoLockedDialogOpen, setArPhotoLockedDialogOpen] = useState(false)
  const [audioGuideLockedDialogOpen, setAudioGuideLockedDialogOpen] = useState(false)

  const appActorRef = AppMachineContext.useActorRef()
  const campaignSheetTypes = useSelector(appActorRef, (s) => s.context.campaignData?.sheetTypes ?? [])
  const { label: stampPointLabel } = useStampPointLabelFromSheetTypes(campaignSheetTypes)

  const api = useApi()

  const handleAudioGuideClick = async (spot: SpotData) => {
    const hasValidGeo = geoLocation.isLoaded && !(geoLocation.lat === 0 && geoLocation.lon === 0)
    const body = hasValidGeo ? { lat: geoLocation.lat, lon: geoLocation.lon } : {}
    try {
      const res = await api.guest.postSpotAudioGuides(spot.id, body)
      if (!res) {
        setAudioGuideLockedDialogOpen(true)
        return
      }
      const entries = res.audioGuides
      if (isAudioGuideLocked(entries)) {
        setAudioGuideLockedDialogOpen(true)
      } else {
        setAudioGuideItems(toAudioGuideDialogItems(entries))
        setAudioGuideSpot(spot)
      }
    } catch {
      setAudioGuideLockedDialogOpen(true)
    }
  }

  if (spots.length === 0) {
    return <p className="p-4 text-center text-gray-400">スポットが見つかりません</p>
  }

  return (
    <>
      <ul data-testid="spot-list" className="h-full overflow-y-auto divide-y divide-gray-100">
        {spots.map((spot) => {
          // 音声ガイド・ARフォトのロック判定はサブエンドポイント呼び出し時にバックエンドが行うため常に false
          const arPhotoLocked = false
          const audioGuideLocked = false
          return (
            <li key={spot.id} data-testid="spot-list-item">
              <SpotOverview
                spot={spot}
                href={gen(`/spots/detail?id=${spot.id}`)}
                sheetNames={spot.sheets}
                tags={spot.tags.map((t) => t.name)}
                hasAudioGuide={spot.audioGuide}
                audioGuideActive={spot.audioGuide}
                audioGuideLocked={false}
                onAudioGuideClick={() => handleAudioGuideClick(spot)}
                hasArPhoto={spot.photoFrame}
                arPhotoLocked={false}
                onArPhotoClick={() => {
                  onArPhotoClick?.(spot)
                }}
                geoLoading={!geoLocation.isLoaded}
              />
            </li>
          )
        })}
      </ul>
      {audioGuideSpot && (
        <DialogSpotsAudioGuide
          scrollLock
          onClose={() => {
            setAudioGuideSpot(null)
            setAudioGuideItems(null)
          }}
          audioGuides={audioGuideItems ?? undefined}
        />
      )}
      {arPhotoLockedDialogOpen && (
        <DialogArPhotoLocked
          scrollLock
          stampLabel={stampPointLabel}
          onClose={() => setArPhotoLockedDialogOpen(false)}
        />
      )}
      {audioGuideLockedDialogOpen && (
        <DialogAudioGuideLocked scrollLock onClose={() => setAudioGuideLockedDialogOpen(false)} />
      )}
    </>
  )
}
